/** BMR = weightCoef×kilo + heightCoef×boy + ageCoef×yaş + constant (erkek ve kadın ayrı) */
export interface BMRCoeffs {
  weightCoefMale: number;
  heightCoefMale: number;
  ageCoefMale: number;
  constantMale: number;
  weightCoefFemale: number;
  heightCoefFemale: number;
  ageCoefFemale: number;
  constantFemale: number;
}

export const BMR_PRESETS: Record<string, BMRCoeffs> = {
  mifflin: {
    weightCoefMale: 10,
    heightCoefMale: 6.25,
    ageCoefMale: -5,
    constantMale: 5,
    weightCoefFemale: 10,
    heightCoefFemale: 6.25,
    ageCoefFemale: -5,
    constantFemale: -161,
  },
  'harris-benedict': {
    weightCoefMale: 13.75,
    heightCoefMale: 5.003,
    ageCoefMale: -6.75,
    constantMale: 66.5,
    weightCoefFemale: 9.563,
    heightCoefFemale: 1.85,
    ageCoefFemale: -4.676,
    constantFemale: 655.1,
  },
  'harris-benedict-revised': {
    weightCoefMale: 13.397,
    heightCoefMale: 4.799,
    ageCoefMale: -5.677,
    constantMale: 88.362,
    weightCoefFemale: 9.247,
    heightCoefFemale: 3.098,
    ageCoefFemale: -4.33,
    constantFemale: 447.593,
  },
};

/** Hazır formüllerin denklem metni (edit dialogda bilgi olarak gösterilir) */
export const BMR_FORMULA_EQUATIONS: Record<string, { erkek: string; kadın: string }> = {
  mifflin: {
    erkek: 'BMR = (10 × kilo) + (6.25 × boy) − (5 × yaş) + 5',
    kadın: 'BMR = (10 × kilo) + (6.25 × boy) − (5 × yaş) − 161',
  },
  'harris-benedict': {
    erkek: 'BMR = 66.5 + (13.75 × kilo) + (5.003 × boy) − (6.75 × yaş)',
    kadın: 'BMR = 655.1 + (9.563 × kilo) + (1.85 × boy) − (4.676 × yaş)',
  },
  'harris-benedict-revised': {
    erkek: 'BMR = 88.362 + (13.397 × kilo) + (4.799 × boy) − (5.677 × yaş)',
    kadın: 'BMR = 447.593 + (9.247 × kilo) + (3.098 × boy) − (4.33 × yaş)',
  },
};

/** Cinsiyet metnini BMR için normalize et (Erkek/Kadın veya erkek/kadın) */
function isMale(gender: string): boolean {
  const g = (gender || '').toLowerCase();
  return g === 'erkek' || g === 'male';
}

export function calculateBMR(
  age: number,
  weight: number,
  height: number,
  gender: string,
  coeffs: BMRCoeffs
): number {
  if (isMale(gender)) {
    return coeffs.weightCoefMale * weight + coeffs.heightCoefMale * height + coeffs.ageCoefMale * age + coeffs.constantMale;
  }
  return coeffs.weightCoefFemale * weight + coeffs.heightCoefFemale * height + coeffs.ageCoefFemale * age + coeffs.constantFemale;
}

const ACTIVITY_MULTIPLIERS: Record<string, number> = {
  sedanter: 1.2,
  hafif: 1.375,
  orta: 1.55,
  agir: 1.725,
  profesyonel: 1.9,
};

export interface TargetNutritionInput {
  age: number;
  weight: number;
  height: number;
  gender: string;
  goal: string;
  activityLevel: string;
}

export interface TargetNutritionResult {
  dailyCalories: number;
  dailyProtein: number;
  dailyCarbs: number;
  dailyFat: number;
}

/** BMR katsayılarına ve danışan bilgisine göre günlük kalori ve makro hesapla */
export function calculateTargetNutrition(
  input: TargetNutritionInput,
  coeffs: BMRCoeffs
): TargetNutritionResult {
  const { age, weight, height, gender, goal, activityLevel } = input;
  const goalLower = (goal || '').toLowerCase();
  const activityKey = (activityLevel || '').toLowerCase().replace(/\s/g, '');

  const bmr = calculateBMR(age, weight, height, gender, coeffs);
  const tdee = bmr * (ACTIVITY_MULTIPLIERS[activityKey] ?? 1.2);

  let targetCalories: number;
  if (goalLower.includes('kilo ver') || goalLower.includes('zayıfla')) {
    targetCalories = tdee - 500;
  } else if (goalLower.includes('kilo al') || goalLower.includes('kas')) {
    targetCalories = tdee + 300;
  } else {
    targetCalories = tdee;
  }

  const proteinPerKg = goalLower.includes('kas') ? 2.0 : 1.8;
  const dailyProtein = Math.round(weight * proteinPerKg);
  const proteinCalories = dailyProtein * 4;
  const fatPercentage = 0.28;
  const dailyFat = Math.round((targetCalories * fatPercentage) / 9);
  const fatCalories = dailyFat * 9;
  const carbCalories = targetCalories - proteinCalories - fatCalories;
  const dailyCarbs = Math.round(carbCalories / 4);

  return {
    dailyCalories: Math.round(targetCalories),
    dailyProtein,
    dailyCarbs,
    dailyFat,
  };
}
